<template>
	<div>
		<div :class="!areAllValuesNull(getValuesArray(props.chartData)) && 'visible'" class="chart_container">
			<canvas :id="chartId"></canvas>
		</div>
		<div
			:class="
				(areAllValuesNull(getValuesArray(props.chartData)) || Object.keys(props.chartData).length === 0) && 'visible'
			"
			class="emptyData_alert"
		>
			데이터 없음
		</div>
	</div>
</template>

<script setup lang="ts">
// core
import { computed, onMounted, ref, watch } from 'vue';
// lib
import { Chart, LinearScaleOptions, registerables, Scale } from 'chart.js';

// store
import { useDisplayStore } from '@/stores/display';

let myChart: Chart | null = null;
Chart.register(...registerables);
type ExtendedScale = Scale & {
	max?: number;
	options: LinearScaleOptions;
};

// -----type-----
type FinanceData = {
	[year: string]: {
		[key: string]: string;
	};
};
type ProcessedData = {
	[key: string]: number[];
};

// -----variable-----
const props = defineProps<{
	sort: string;
	chartData?: any;
}>();

const displayStore = useDisplayStore();
const chartId = ref('chart-' + Math.random().toString(36).substring(2, 9));
const clickState = ref<{ [key: number]: boolean }>({});
const clickedDataset = ref();
const clickedDataConfig = ref();

const leftAxisData = ref<string[]>([]);
const rightAxisData = ref<string[]>([]);

const hasLeftAxisData = computed(() => leftAxisData.value.length > 0);
const hasRightAxisData = computed(() => rightAxisData.value.length > 0);

// -----function-----
// 차트 데이터 가공 함수
const processChartData = (data: FinanceData) => {
	const years = Object.keys(data);

	const chartItems = years.map((key) => {
		return data[key];
	});

	const names = Object.keys(chartItems[0]).map((data) => {
		return getLegendName(data);
	});

	const keys = Object.keys(chartItems[0]);

	const values: ProcessedData = {};
	// 각 연도별 데이터를 순회하면서 모든 key를 동적으로 처리
	Object.values(data).forEach((yearData) => {
		Object.keys(yearData).forEach((key) => {
			// 만약 해당 key가 values에 없다면 초기화
			if (!values[key]) {
				values[key] = [];
			}
			// 각 항목의 value를 values의 해당 key에 추가
			values[key].push(Number(yearData[key]));
		});
	});

	const originValues: any = {};
	// 각 연도별 데이터를 순회하면서 모든 key를 동적으로 처리
	Object.values(data).forEach((yearData) => {
		Object.keys(yearData).forEach((key) => {
			// 만약 해당 key가 values에 없다면 초기화
			if (!originValues[key]) {
				originValues[key] = [];
			}
			// 각 항목의 value를 values의 해당 key에 추가
			originValues[key].push(yearData[key]);
		});
	});

	return {
		years,
		names,
		keys,
		values,
		originValues,
	};
};

// 양축과 가로선 개수를 일치시키기 위해 stepSize를 계산하는 로직
const computeStepSize = () => {
	console.log('myChart:', myChart);
	if (myChart) {
		const leftScale = myChart.scales.yLeft as ExtendedScale;
		const rightScale = myChart.scales.yRight as ExtendedScale;

		let attempts = 0; // 반복 횟수를 추적
		const maxAttempts = 50; // 최대 반복 횟수

		while (
			(myChart.scales.yLeft.ticks.length !== 6 || myChart.scales.yRight.ticks.length !== 6) &&
			attempts < maxAttempts
		) {
			// 왼쪽 스케일 검증 및 업데이트
			if (myChart.scales.yLeft.ticks.length !== 6) {
				leftScale.options.ticks.stepSize = (myChart.scales.yLeft.max - myChart.scales.yLeft.min) / 5;
			}

			// 오른쪽 스케일 검증 및 업데이트
			if (myChart.scales.yRight.ticks.length !== 6) {
				rightScale.options.ticks.stepSize = (myChart.scales.yRight.max - myChart.scales.yRight.min) / 5;
			}

			// 차트를 업데이트하여 변경 사항 적용
			myChart.update();

			// 반복 횟수 증가
			attempts++;
		}

		// 최대 반복 횟수에 도달한 경우 로그 출력
		if (attempts === maxAttempts) {
			console.warn('축 6개 만들기 실패');
		}
	}
};

// 차트 만들고 축 커스텀 후 랜더링하는 함수
const getChart = async (key: string, data: any) => {
	if (data.length === 0) {
		return;
	}

	createChartItem(document.getElementById(chartId.value) as HTMLCanvasElement, data, key);

	computeStepSize();
};

// 차트 타입 확인 함수
const getChartType = (key: string) => {
	switch (key) {
		case '매출액':
		case '배당금':
			return 'bar'; // '매출액'과 '배당금'은 bar 차트
		default:
			return 'line'; // 그 외는 line 차트
	}
};

// 레이블 색상 확인 함수
const getLabelColor = (key: string) => {
	switch (key) {
		case '매출액영업이익':
		case '영업이익률당기순이익률':
		case '매출액영업이익증가율':
			return { left: '#fff', right: '#fff' };
		case 'PER과EPS':
		case 'PBR과BPS':
		case 'ROE과ROIC':
		case '유동비율당좌비율':
		case '부채비율':
			return { left: '#7BEBFF', right: '#C891FF' };
		case '배당':
			return { left: '#7BEBFF', right: '#fff' };
	}
};

// 범례 이름 확인 함수
const getLegendName = (key: string) => {
	if (key === '배당금') {
		return '배당금(우, 엔)';
	} else if (key === '배당수익률(%)') {
		return '배당수익률(좌, %)';
	} else if (key === '주가수익비율(PER)') {
		return 'PER(좌, 배)';
	} else if (key === '주당_순이익(EPS)') {
		return 'EPS(우)';
	} else if (key === '주가순자산비율(PBR)') {
		return 'PBR(좌, 배)';
	} else if (key === '주당_순자산(BPS)') {
		return 'BPS(우)';
	} else if (key === '자기자본_이익률(ROE)') {
		return 'ROE(좌)';
	} else if (key === '투하자본_이익률(ROIC)') {
		return 'ROIC(우)';
	} else if (key === '부채비율') {
		return '부채비율';
	} else if (key === '유동비율') {
		return '유동비율(좌)';
	} else if (key === '당좌비율') {
		return '당좌비율(우)';
	} else if (key === '영업이익률') {
		return '영업이익률';
	} else if (key === '당기순이익률') {
		return '당기순이익률';
	} else if (key === '매출액') {
		return '매출액(우)';
	} else if (key === '영업이익') {
		return '영업이익(좌)';
	} else if (key === '당기순이익') {
		return '당기순이익(좌)';
	} else if (key === '매출액_증가율') {
		return '매출액 증가율';
	} else if (key === '영업이익_증가율') {
		return '영업이익 증가율';
	} else if (key === '당기순이익_증가율') {
		return '당기순이익 증가율';
	} else {
		return key; // 기본적으로 원래 값을 리턴
	}
};

// 레이블 축 확인 함수
const getAxisID = (key: string) => {
	switch (key) {
		case '영업이익':
		case '당기순이익':
		case '영업이익률':
		case '당기순이익률':
		case '매출액_증가율':
		case '영업이익_증가율':
		case '당기순이익_증가율':
		case '유동비율':
		case '부채비율':
		case '주가수익비율(PER)':
		case '주가순자산비율(PBR)':
		case '자기자본_이익률(ROE)':
		case '배당수익률(%)':
			return 'yLeft';
		case '매출액':
		case '당좌비율':
		case '주당_순이익(EPS)':
		case '주당_순자산(BPS)':
		case '투하자본_이익률(ROIC)':
		case '배당금':
			return 'yRight';
	}
};

// 차트 컬러 확인 함수
const getChartColor = (key: string) => {
	switch (key) {
		case '매출액':
		case '배당금':
			return 'rgb(80, 97, 124, 1)'; // bar 차트일 때의 색상
		case '영업이익':
		case '영업이익률':
		case '매출액_증가율':
		case '부채비율':
		case '유동비율':
		case '주가수익비율(PER)':
		case '주가순자산비율(PBR)':
		case '자기자본_이익률(ROE)':
		case '배당수익률(%)':
			return 'rgba(123, 235, 255, 1)'; // 하늘색
		case '당기순이익':
		case '당기순이익률':
		case '영업이익_증가율':
		case '당좌비율':
		case '주당_순이익(EPS)':
		case '주당_순자산(BPS)':
		case '투하자본_이익률(ROIC)':
			return 'rgba(200, 145, 255, 1)'; // 보라색
		case '당기순이익_증가율':
			return 'rgba(255, 189, 113, 1)'; // 노란색
		default:
			return 'rgba(0, 0, 0, 1)'; // 기본 색상
	}
};

// 투명도 설정 함수
const setOpacity = (color: any, opacity: any) => {
	// backgroundColor와 borderColor의 경우 배열 또는 단일 값일 수 있으므로 이를 처리
	if (Array.isArray(color)) {
		return color.map((c) => c.replace(/rgba?\((\d+), (\d+), (\d+),? ([\d.]+)?\)/, `rgba($1, $2, $3, ${opacity})`));
	}
	return color.replace(/rgba?\((\d+), (\d+), (\d+),? ([\d.]+)?\)/, `rgba($1, $2, $3, ${opacity})`);
};

// 데이터없음 문구 예외처리 로직
// 데이터없음 문구 예외처리 관련: 데이터 타입
type DebtRatioData = {
	[year: string]: {
		[key: string]: string | null;
	};
};
// 데이터없음 문구 예외처리 관련: 값 추출하여 배열에 담는 함수
const getValuesArray = (data: DebtRatioData): (string | null)[] => {
	const result = [];
	for (const year in data) {
		const values = data[year];
		for (const key in values) {
			result.push(values[key]);
		}
	}
	return result;
};
// 데이터없음 문구 예외처리 관련: 모든 값이 null인지 확인하는 함수
const areAllValuesNull = (arr: (string | null)[] | null): boolean => {
	if (!Array.isArray(arr)) {
		return false;
	}

	return arr.every((value) => value === null);
};

// 차트 생성, 옵션 설정 및 커스텀하는 함수
const createChartItem = (ctx: HTMLCanvasElement | null, data: FinanceData, key: string) => {
	const chartData = processChartData(data);
	const labelColor = getLabelColor(key);

	if (ctx) {
		const datasets: any = chartData.keys.map((key, index) => {
			const chartType = getChartType(key);
			const chartColor = getChartColor(key);
			const axisID = getAxisID(key);
			if (axisID === 'yLeft') {
				leftAxisData.value.push(key);
			} else if (axisID === 'yRight') {
				rightAxisData.value.push(key);
			}
			return {
				type: chartType,
				label: chartData.names[index],
				spanGaps: true,
				data: chartData.values[key],
				backgroundColor: chartColor,
				borderColor: chartColor,
				order: chartType === 'bar' ? 2 : 1,
				yAxisID: axisID,
				barThickness: chartType === 'bar' ? (!displayStore.isSmallScreen ? 32 : 22) : null,
				pointRadius: 0,
				key: key, // key 추출 용이하게 하기 위해 추가 // Chart 옵션에는 안쓰임
			};
		});

		const isRightAxisIncluded = datasets.some((data: any) => data.yAxisID == 'yRight');
		myChart = new Chart(ctx, {
			data: {
				datasets,
				labels: chartData.years,
			},
			options: {
				onClick: () => {
					// 📌 데이터 3개 미만 차트 중 한쪽 데이터만 없는 경우
					if (clickState.value && (hasLeftAxisData.value || hasRightAxisData.value) && chartData.keys.length < 3) {
						myChart?.data.datasets.forEach((dataset) => {
							if (clickedDataset.value) {
								clickedDataset.value.hidden = false;
							}
							dataset.backgroundColor = setOpacity(dataset.backgroundColor, 1);
							dataset.borderColor = setOpacity(dataset.borderColor, 1);
						});
						if (!hasLeftAxisData.value) {
							clickedDataConfig.value.options.scales.yLeft.display = true;
						} else if (!hasRightAxisData.value && isRightAxisIncluded) {
							clickedDataConfig.value.options.scales.yRight.display = true;
						}
						leftAxisData.value = [];
						if (isRightAxisIncluded) {
							rightAxisData.value = [];
						}
						chartData.keys.forEach((key) => {
							const axisID = getAxisID(key);
							if (axisID === 'yLeft') {
								leftAxisData.value.push(key);
							}
							if (axisID === 'yRight' && isRightAxisIncluded) {
								rightAxisData.value.push(key);
							}
						});
						clickState.value = {};
						myChart?.update();
					}
					// 📌 양쪽 데이터 모두 없는 경우 OR 데이터 3개 이상 차트 중 한쪽 데이터만 없는 경우
					else if (
						clickState.value &&
						((!hasLeftAxisData.value && !hasRightAxisData.value) || chartData.keys.length >= 3)
					) {
						const datasetsArray = myChart?.legend?.chart.data.datasets;
						const yLeftObj = myChart?.legend?.chart.config.options?.scales?.yLeft;
						const yRightObj = myChart?.legend?.chart.config.options?.scales?.yRight;
						datasetsArray?.forEach((dataset) => {
							dataset.hidden = false;
							dataset.backgroundColor = setOpacity(dataset.backgroundColor, 1);
							dataset.borderColor = setOpacity(dataset.borderColor, 1);
						});
						if (yLeftObj) {
							yLeftObj.display = true;
						}
						if (yRightObj && isRightAxisIncluded) {
							yRightObj.display = true;
						}
						leftAxisData.value = [];
						rightAxisData.value = [];
						chartData.keys.forEach((key) => {
							const axisID = getAxisID(key);
							if (axisID === 'yLeft') {
								leftAxisData.value.push(key);
							} else {
								rightAxisData.value.push(key);
							}
						});
						clickState.value = {};
						myChart?.update();
					}
				},
				// 반응형 대응하기 위한 속성
				responsive: true,
				maintainAspectRatio: true,
				aspectRatio: !displayStore.isSmallScreen ? 1.6 : 0.833,
				plugins: {
					tooltip: {
						enabled: true,
						intersect: false,
						callbacks: {
							// 툴팁의 타이틀
							title: (context) => {
								return `${context[0].label}`;
							},
							// 툴팁의 본문
							label: (context) => {
								const value = context.raw;
								return chartData.originValues[datasets[context.datasetIndex].key][context.dataIndex] !== null
									? `${context.dataset.label}: ${value}`
									: `${context.dataset.label}: ${'데이터 없음'}`;
							},
						},
					},
					legend: {
						position: 'bottom',
						align: !displayStore.isSmallScreen ? 'center' : 'center',
						labels: {
							boxHeight: 0,
							generateLabels: function (chart) {
								const originalLabels = Chart.defaults.plugins.legend.labels.generateLabels(chart);
								return originalLabels.map((label) => {
									label.lineJoin = 'miter';
									if (label.text === '매출액(우)' || label.text === '배당금(우, 엔)') {
										label.lineWidth = 10; // 범례 박스 선의 두께
									}
									return label;
								});
							},
							padding: !displayStore.isSmallScreen ? 32 : 16,
							font: {
								size: 12,
								weight: 'normal',
								lineHeight: 1.17,
							},
							color: '#fff',
						},
						onClick: (_e, legendItem, legend) => {
							const datasetIndex = legendItem.datasetIndex;
							if (datasetIndex === undefined) return;
							clickedDataset.value = legend.chart.data.datasets[datasetIndex];
							clickedDataConfig.value = legend.chart.config;
							// 클릭 상태 확인
							// 📌 첫 번째 클릭 - 해당 차트 숨기고 나머지는 선명하게 유지
							if (!clickState.value[datasetIndex]) {
								clickState.value[datasetIndex] = true;
								// 차트 숨기기
								clickedDataset.value.hidden = true;
								// yAxis 커스텀
								legend.chart.data.datasets.forEach((dataset: any, idx: number) => {
									// 범례 클릭으로 그래프가 숨겨질 때, 특정 조건에 따라 yAxis도 삭제시켜주는 작업
									if (idx === datasetIndex) {
										if (dataset.yAxisID === 'yLeft') {
											if (leftAxisData.value.includes(dataset.key)) {
												const index = leftAxisData.value.indexOf(dataset.key);
												leftAxisData.value.splice(index, 1);
												// console.log('left/삭제:', leftAxisData.value);
												// 왼쪽 축 요소에 직접 접근하여 display 속성 조작
												if (
													dataset.yAxisID &&
													legend.chart.config.options?.scales?.[dataset.yAxisID] &&
													!hasLeftAxisData.value
												) {
													(legend.chart.config.options.scales[dataset.yAxisID] as any).display = hasLeftAxisData.value;
												}
											}
										} else {
											if (rightAxisData.value.includes(dataset.key)) {
												const index = rightAxisData.value.indexOf(dataset.key);
												rightAxisData.value.splice(index, 1);
												// console.log('right/삭제:', rightAxisData.value);
												// 오른쪽 축 요소에 직접 접근하여 display 속성 조작
												if (
													dataset.yAxisID &&
													legend.chart.config.options?.scales?.[dataset.yAxisID] &&
													!hasRightAxisData.value
												) {
													(legend.chart.config.options.scales[dataset.yAxisID] as any).display = hasRightAxisData.value;
												}
											}
										}
									} else {
										dataset.backgroundColor = setOpacity(dataset.backgroundColor, 1);
										dataset.borderColor = setOpacity(dataset.borderColor, 1);
									}
								});
							}
							// 📌 두 번째 클릭 - 해당 차트를 강조하고 나머지 차트는 흐리게
							else {
								clickState.value[datasetIndex] = false;
								// 차트 다시 표시
								clickedDataset.value.hidden = false;
								// 그래프 강조 효과 & yAxis 커스텀
								legend.chart.data.datasets.forEach((dataset: any, idx) => {
									// 선택된 차트 투명도 복원
									if (idx === datasetIndex) {
										dataset.backgroundColor = setOpacity(dataset.backgroundColor, 1);
										dataset.borderColor = setOpacity(dataset.borderColor, 1);
									}
									// 나머지 차트 투명도 낮추기
									else {
										dataset.backgroundColor = setOpacity(dataset.backgroundColor, 0.1);
										dataset.borderColor = setOpacity(dataset.borderColor, 0.1);
									}
									// 범례 클릭으로 그래프가 강조될 때, 특정 조건에 따라 yAxis를 복구시켜주는 작업
									if (idx === datasetIndex) {
										if (dataset.yAxisID === 'yLeft') {
											if (!leftAxisData.value.includes(dataset.key)) {
												leftAxisData.value.push(dataset.key);
												// console.log('left/추가:', leftAxisData.value);
												if (
													dataset.yAxisID &&
													legend.chart.config.options?.scales?.[dataset.yAxisID] &&
													hasLeftAxisData.value
												) {
													(legend.chart.config.options.scales[dataset.yAxisID] as any).display = hasLeftAxisData.value;
												}
											}
										} else {
											if (!rightAxisData.value.includes(dataset.key)) {
												rightAxisData.value.push(dataset.key);
												// console.log('right/추가:', rightAxisData.value);
												if (
													dataset.yAxisID &&
													legend.chart.config.options?.scales?.[dataset.yAxisID] &&
													hasRightAxisData.value
												) {
													(legend.chart.config.options.scales[dataset.yAxisID] as any).display = hasRightAxisData.value;
												}
											}
										}
									}
								});
							}
							legend.chart.update();
						},
					},
				},
				interaction: {
					mode: 'index',
					intersect: false,
				},
				layout: {
					padding: {
						top: 20,
					},
				},
				scales: {
					// 연도 레이블
					x: {
						ticks: {
							color: '#fff',
							font: {
								size: 12,
								weight: 'normal',
							},
							padding: !displayStore.isSmallScreen ? 0 : 16,
						},
						grid: {
							display: false,
						},
						offset: true,
					},
					y: {
						ticks: {
							display: false,
							maxTicksLimit: 6,
						},
						grid: {
							color: '#393D45', // 그리드 선 색상
							drawBorder: false,
						},
					},
					// 좌측 데이터 레이블
					yLeft: {
						display: hasLeftAxisData.value, // 갑 변경 시 업데이트 필요
						type: 'linear',
						position: 'left',
						beginAtZero: true,
						ticks: {
							color: labelColor?.left,
							font: {
								size: 12,
								weight: 'normal',
								lineHeight: 1.17,
							},
							maxTicksLimit: 6,
						},
						grid: {
							drawOnChartArea: false,
						},
					},
					// 우측 데이터 레이블
					yRight: {
						display: hasRightAxisData.value, // 값 변경 시 업데이트 필요
						type: 'linear',
						position: 'right',
						beginAtZero: true,
						ticks: {
							color: labelColor?.right,
							font: {
								size: 12,
								weight: 'normal',
								lineHeight: 1.17,
							},
							maxTicksLimit: 6,
						},
						grid: {
							drawOnChartArea: false,
						},
					},
				},
			},
		});
	}
};

// -----composition-----
watch(
	() => props.chartData,
	() => {
		myChart?.destroy();
		if (Object.keys(props.chartData)) {
			getChart(props.sort, props.chartData);
		}
	}
);

onMounted(() => {
	if (Object.keys(props.chartData)) {
		// console.log('props.chartData:', props.sort);
		getChart(props.sort, props.chartData);
		// console.log('--------------------------myChart--------------------------', myChart);
		// console.log('LEFT max:', myChart?.scales.yLeft.max);
		// console.log('LEFT min:', myChart?.scales.yLeft.min);
		// console.log('LEFT RESULT:', (Number(myChart?.scales.yLeft.max) - Number(myChart?.scales.yLeft.min)) / 5);
		// console.log('--------------------------');
		// console.log('RIGHT max:', myChart?.scales.yRight.max);
		// console.log('RIGHT min:', myChart?.scales.yRight.min);
		// console.log('RIGHT RESULT:', (Number(myChart?.scales.yRight.max) - Number(myChart?.scales.yRight.min)) / 5);
		// console.log('--------------------------');
		// console.log('yLeft stepSize:', myChart?.scales.yLeft.options.ticks.stepSize);
		// console.log('yRight stepSize:', myChart?.scales.yRight.options.ticks.stepSize);
		// console.log('--------------------------');
		// console.log('yLeft.ticks.length:', myChart?.scales.yLeft.ticks);
		// console.log('yRight.ticks.length:', myChart?.scales.yRight.ticks);
		// console.log('yLeft.ticks.length:', myChart?.scales.yLeft.ticks);
		// console.log('yRight.ticks.length:', myChart?.scales.yRight.ticks);
		// console.log('maxTicksLimit:', myChart?.scales.y.options.ticks.maxTicksLimit);
	}
});
</script>

<style scoped lang="scss">
.chart_container {
	display: none;
	width: 100%;
}
.emptyData_alert {
	display: none;
	padding: 3.2rem 0rem;
	font-size: var(--font-s-regular);
	font-weight: var(--font-w-light);
	line-height: 1.2;
	text-align: center;
}
.visible {
	display: block;
}
</style>
